/* ============================================================
   v2 "command console" design tokens. These reconcile the
   prototype's operational-console treatment with the app's
   existing navy/cyan palette (extraColors.status.navyLight is
   already #37B2FF). Kept in one place so the v2 components share
   a single source of truth.
   ============================================================ */
import { FONT_FAMILY } from "../../../utils/theme"

/**
 * Type follows the design brief mapped onto the repo's bound design system:
 * the DS sans (the app's `FONT_FAMILY`) for labels/body, and a tabular mono
 * stack for numerics. The prototype names "Arimo", but the handoff says to
 * prefer the repo's DS tokens — so we use the app font here for consistency
 * with the rest of the product.
 */
export const V2_FONT_SANS = FONT_FAMILY
export const V2_FONT_MONO =
  'ui-monospace, "SFMono-Regular", Menlo, Consolas, monospace'

export const V2_SURFACE = {
  /** App background behind the console. */
  app: "#080B11",
  /** Translucent panel fill. */
  panel: "rgba(18,24,33,0.74)",
  /** Solid panel fill. */
  panelSolid: "#11161E",
  hairline: "rgba(255,255,255,0.07)",
  hairlineStrong: "rgba(255,255,255,0.13)",
} as const

export const V2_ACCENT = {
  /** Interactive / data accent (cyan). */
  cyan: "#37B2FF",
  cyanSoft: "rgba(55,178,255,0.16)",
  /** Ambient / status / label accent (green). */
  green: "#2DE0A6",
  /** Gold — pinned/ongoing. */
  gold: "#FFCE64",
  /** Reject — sub-threshold dots flash this before they burn off the scan map. */
  reject: "#FF5C5C",
} as const

/** Text ramp from the prototype. */
export const V2_TEXT = {
  body: "#D5DAE0",
  muted: "#677789",
  disabled: "#485667",
} as const

/** Foreground used on filled accent/severity surfaces (badges, chips, buttons). */
export const V2_ON_ACCENT = "#06121F"

/** Severity ramp — value via brightness, not hue (cyan luminance). */
export type V2Tier = "severe" | "high" | "moderate" | "low"

export const V2_SEVERITY_COLOR: Record<V2Tier, string> = {
  severe: "#7EE6FF",
  high: "#3CC4EC",
  moderate: "#2E94B8",
  low: "#2A5C73",
}

export const V2_SEVERITY_LABEL: Record<V2Tier, string> = {
  severe: "SEVERE",
  high: "HIGH",
  moderate: "MODERATE",
  low: "LOW",
}

export const V2_SEVERITY_ORDER: V2Tier[] = ["severe", "high", "moderate", "low"]

/** NWSS status colors — wastewater is colored by status, not severity. */
export type V2NwssStatus = "start" | "ongoing" | "ended"

export const V2_STATUS_COLOR: Record<V2NwssStatus, string> = {
  start: "#2DE0A6",
  ongoing: "#FFCE64",
  ended: "#6B7785",
}

export const V2_STATUS_LABEL: Record<V2NwssStatus, string> = {
  start: "NEW",
  ongoing: "ONGOING",
  ended: "ENDED",
}

/**
 * Eyebrow/label type treatment used throughout the console chrome. The brief
 * specs ~9px labels, but that's below an accessible reading size, so the
 * console floors eyebrow text at 12px while keeping the uppercase/tracked look.
 */
export const V2_EYEBROW = {
  fontFamily: V2_FONT_SANS,
  textTransform: "uppercase" as const,
  fontSize: "0.75rem", // 12px — accessible floor (brief called for ~9px)
  letterSpacing: "0.12em",
  fontWeight: 700,
  lineHeight: 1.3,
}

/** Tabular numeric treatment for metrics, scores, counts. */
export const V2_NUMERIC = {
  fontFamily: V2_FONT_MONO,
  fontVariantNumeric: "tabular-nums" as const,
  fontWeight: 700,
}

/** Body copy size — 14px (brief called for 13px; bumped for readability). */
export const V2_BODY_FONT_SIZE = "0.875rem"

/** Smallest metadata text on the page (dates, sub-lines). Accessible floor. */
export const V2_META_FONT_SIZE = "0.8125rem" // 13px
